import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import { AppDataContext } from '~/contexts/AppDataContext';
import { ArticleContext } from '~/contexts/ArticleContext';
import {
  getCSSColorBorder,
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
  getCSSTextAlign,
} from '~/utilities/getCSS';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';

const cssClasses = cva('italic flex flex-col', {
  variants: {
    position: {
      top: 'mb-4',
      bottom: 'mt-4',
    },
    align: getCSSTextAlign(),
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

const borderClasses = cva('content-[""]', {
  variants: {
    borderColor: getCSSColorBorder(),
    showDividersInLargeScreen: {
      true: 'lg:border-t',
      false: '',
    },
    showDividersInMediumScreen: {
      true: 'md:max-lg:border-t',
      false: '',
    },
    showDividersInSmallScreen: {
      true: 'max-md:border-t',
      false: '',
    },
  },
});

export default component$(({ position }: { position: string }): JSX.Element => {
  const { payloadToSSR } = useContext(AppDataContext);
  const article = useContext(ArticleContext);
  const {
    themeComponents: { articleDisclaimer },
  } = useContext(AppDataContext);

  if (!article) return <></>;

  const {
    textColor,
    dividersColor,
    showDividersInLargeScreen,
    showDividersInMediumScreen,
    showDividersInSmallScreen,
    textAlignment,
  } = articleDisclaimer;

  const shouldDisplayDisclaimer =
    article.metadata?.commercialTags?.includes('disclaimer');
  const shouldDisplayReview =
    article.metadata?.commercialTags?.includes('review-disclaimer');
  const disclaimer = shouldDisplayDisclaimer
    ? payloadToSSR.pageContent.pageSettings?.disclaimerText?.value
    : '';
  const reviewDisclaimer = shouldDisplayReview
    ? payloadToSSR.pageContent.pageSettings?.reviewDisclaimer?.value
    : '';
  const displayBottom =
    payloadToSSR.pageContent.pageSettings?.shouldDisplayDisclaimerAtBottom
      ?.value;

  if (
    (displayBottom && position === 'top') ||
    (!displayBottom && position === 'bottom')
  )
    return <></>;

  return (
    <>
      {[disclaimer, reviewDisclaimer].map((text, index) => {
        if (!text) return <></>;

        return (
          <div
            key={index}
            class={cssClasses({
              position: !displayBottom ? 'top' : 'bottom',
              align: textAlignment.value,
              color: textColor.value,
              ...getFontSizeObject(articleDisclaimer, 'fontSize'),
            })}
          >
            <span
              class={borderClasses({
                borderColor: dividersColor.value,
                showDividersInLargeScreen: showDividersInLargeScreen.value,
                showDividersInMediumScreen: showDividersInMediumScreen.value,
                showDividersInSmallScreen: showDividersInSmallScreen.value,
              })}
            />
            <small class='py-2.5 scroll-mt-5'>{text}</small>
            <span
              class={borderClasses({
                borderColor: dividersColor.value,
                showDividersInLargeScreen: showDividersInLargeScreen.value,
                showDividersInMediumScreen: showDividersInMediumScreen.value,
                showDividersInSmallScreen: showDividersInSmallScreen.value,
              })}
            />
          </div>
        );
      })}
    </>
  );
});
